/**
 * SD-2708: preparing rendered HTML to survive a paste into a mail client.
 *
 * The clipboard carries markup, not our stylesheet, so anything styled by class or by the app's
 * CSS variables arrives unstyled. Mail clients compound this: they drop `<style>` blocks and
 * class-based rules and honour only inline `style` attributes, then apply their own defaults to
 * whatever is left. Smoke testing showed exactly that — an `<h2>` pasted into Outlook kept the
 * element's bold but lost every trace of heading size, and an unstyled table would arrive with no
 * borders at all.
 *
 * So the clipboard copy gets its own pass that writes the styling inline. Deliberately literal
 * values rather than the theme's CSS variables: `var(--app-...)` cannot resolve once the markup
 * has left the application, and would degrade to no style at all. Colours are kept neutral so the
 * result sits acceptably on the white background every mail composer uses.
 *
 * Applies only to the copied flavour. The on-screen preview is styled by the component's own
 * stylesheet and is left untouched.
 */

/**
 * SD-2452's table chrome pastes as text, since its icons are mat-icon ligature spans:
 * `open_in_fullExpand`, `downloadDownload CSV`. `button` is listed so later controls
 * are dropped by default.
 */
const NON_EMAIL_SELECTORS = [
  'button',
  '.qt-table-block__bar',
  '.qt-table-block__foot',
].join(', ');

/** Inline styles by tag, chosen to read the same as the in-app preview once pasted. */
const EMAIL_STYLES: Record<string, string> = {
  p: 'margin:0 0 10px;',
  ul: 'margin:0 0 10px;padding-left:22px;',
  ol: 'margin:0 0 10px;padding-left:22px;',
  li: 'margin:0 0 4px;',
  table: 'border-collapse:collapse;margin:10px 0;',
  th: 'border:1px solid #d0d0d0;padding:6px 10px;background:#f2f2f2;text-align:left;font-weight:600;',
  td: 'border:1px solid #d0d0d0;padding:6px 10px;text-align:left;',
  blockquote:
    'margin:10px 0;padding-left:12px;border-left:3px solid #d0d0d0;color:#555555;',
  code: 'font-family:Consolas,Menlo,monospace;font-size:13px;',
};

/**
 * Sizes for what were heading elements, applied to the paragraphs that replace them.
 *
 * Headings do not survive as headings. Outlook renders through Word, which maps `h1`-`h6` onto its
 * own built-in Heading styles and beats an inline `font-size` — smoke testing showed a styled
 * `<h2>` still arriving at body size, while inline styles on table cells came through untouched in
 * the same paste. Word has no competing style for a paragraph, so the size sticks when the heading
 * element is gone.
 */
const HEADING_STYLES: Record<string, string> = {
  h1: 'font-size:20px;font-weight:700;',
  h2: 'font-size:17px;font-weight:700;',
  h3: 'font-size:15px;font-weight:700;',
  h4: 'font-size:14px;font-weight:700;',
  h5: 'font-size:14px;font-weight:700;',
  h6: 'font-size:14px;font-weight:700;',
};

/** Spacing stays on the paragraph; only the size and weight need to be character-level. */
const HEADING_SPACING: Record<string, string> = {
  h1: 'margin:16px 0 8px;',
  h2: 'margin:14px 0 6px;',
  h3: 'margin:12px 0 6px;',
  h4: 'margin:12px 0 6px;',
  h5: 'margin:12px 0 6px;',
  h6: 'margin:12px 0 6px;',
};

/**
 * Return the same markup with presentation written inline. Existing `style` attributes win, so
 * anything the renderer set deliberately is preserved rather than overwritten.
 */
export function toEmailHtml(html: string): string {
  if (!html?.trim()) return '';

  const doc = new DOMParser().parseFromString(html, 'text/html');

  for (const el of Array.from(doc.body.querySelectorAll(NON_EMAIL_SELECTORS))) {
    el.remove();
  }

  for (const [tag, style] of Object.entries(EMAIL_STYLES)) {
    for (const el of Array.from(doc.body.querySelectorAll(tag))) {
      const existing = el.getAttribute('style') ?? '';
      el.setAttribute('style', existing ? `${style}${existing}` : style);
    }
  }

  replaceHeadingsWithStyledParagraphs(doc);

  return doc.body.innerHTML;
}

/**
 * Swap each heading for a paragraph whose text is wrapped in a sized span.
 *
 * The span is the part that matters. Word separates paragraph formatting from character
 * formatting and character formatting wins, so a `font-size` on the block is still overridden by
 * its own style while the same size on an inline span is honoured. The paragraph keeps the spacing
 * either way, and clients that never had the conflict render the two identically.
 */
function replaceHeadingsWithStyledParagraphs(doc: Document): void {
  for (const [tag, style] of Object.entries(HEADING_STYLES)) {
    for (const heading of Array.from(doc.body.querySelectorAll(tag))) {
      const paragraph = doc.createElement('p');
      const text = doc.createElement('span');
      text.innerHTML = heading.innerHTML;
      text.setAttribute('style', style);
      paragraph.appendChild(text);
      paragraph.setAttribute('style', HEADING_SPACING[tag] ?? '');
      heading.replaceWith(paragraph);
    }
  }
}
